<template>
  <div>
    <div class="main-content">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-body">
              <ValidationObserver ref="formObserver" v-slot="{ validate }">
                <b-form @submit.prevent="handleSubmit(validate)">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                        <label for="rule-name">
                          <span class="required">*</span> {{ $t('name') }}:
                        </label>
                        <b-form-input
                            id="rule-name"
                            v-model="rule.name"
                            maxlength="120"
                            :state="errors.length ? false : null"
                            :placeholder="$t('ex.: Excesso de velocidade')"
                        />
                        <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
                      </ValidationProvider>
                    </div>
                    <div class="form-group col-md-6">
                      <label>{{ $t('Evento do Movias') }}:</label>
                      <v-select
                          v-model="rule.movias_event_id"
                          :options="eventOptions"
                          :reduce="event => event.id"
                          label="label"
                          :loading="loadingEvents"
                          :placeholder="$t('Qualquer evento')"
                      />
                      <small class="text-muted">
                        {{ $t('Vazio = qualquer posição do Movias, filtrada só pelas condições.') }}
                      </small>
                    </div>
                  </div>

                  <h6 class="mt-1 mb-50">{{ $t('Condições') }}</h6>
                  <p class="text-muted small mb-1">
                    {{ $t('Todas precisam ser verdadeiras na mesma posição. Campo que o Movias não mandar não dispara.') }}
                  </p>
                  <div v-for="(condition, index) in rule.conditions" :key="index" class="form-row align-items-center mb-50">
                    <div class="col-md-4">
                      <b-form-select
                          v-model="condition.field"
                          :options="fieldOptions"
                          size="sm"
                          @change="onFieldChange(condition)"
                      />
                    </div>
                    <div class="col-md-2">
                      <b-form-select v-model="condition.operator" :options="operatorsOf(condition.field)" size="sm" />
                    </div>
                    <div class="col-md-4">
                      <b-form-select
                          v-if="isBoolean(condition.field)"
                          v-model="condition.value"
                          :options="booleanOptions(condition.field)"
                          size="sm"
                      />
                      <b-input-group v-else size="sm" :append="unitOf(condition.field)">
                        <b-form-input v-model="condition.value" type="number" step="any" />
                      </b-input-group>
                    </div>
                    <div class="col-md-2">
                      <b-button variant="outline-danger" size="sm" @click="rule.conditions.splice(index, 1)">
                        <i class="fa fa-trash" />
                      </b-button>
                    </div>
                  </div>
                  <b-button variant="outline-primary" size="sm" class="mb-2" @click="addCondition">
                    <i class="fa fa-plus" /> {{ $t('Adicionar condição') }}
                  </b-button>

                  <div class="form-row">
                    <div class="form-group col-md-3">
                      <label><span class="required">*</span> {{ $t('Criticidade') }}:</label>
                      <b-form-select v-model="rule.criticality" :options="criticalityOptions" />
                    </div>
                    <div class="form-group col-md-3">
                      <label>{{ $t('Intervalo mínimo entre alertas') }}:</label>
                      <b-input-group append="min">
                        <b-form-input v-model.number="rule.cooldown_minutes" type="number" min="0" max="1440" />
                      </b-input-group>
                      <small class="text-muted">{{ $t('Por veículo. 0 = alerta a cada posição.') }}</small>
                    </div>
                    <div class="form-group col-md-3 d-flex align-items-center">
                      <b-form-checkbox v-model="rule.active" switch class="mt-1">
                        {{ $t('Ativo') }}
                      </b-form-checkbox>
                    </div>
                  </div>

                  <h6 class="mt-1 mb-50">{{ $t('Onde vale') }}</h6>
                  <p class="text-muted small mb-1">{{ $t('Vazio = todos os veículos da empresa.') }}</p>
                  <div class="form-row">
                    <div class="form-group col-md-4">
                      <label>{{ $t('Central') }}:</label>
                      <v-select
                          v-model="rule.center_id"
                          :options="centers"
                          :reduce="center => center.id"
                          label="name"
                          :placeholder="$t('Todas')"
                          @input="onCenterChange"
                      />
                    </div>
                    <div class="form-group col-md-4">
                      <label>{{ $t('Unidade') }}:</label>
                      <v-select
                          v-model="rule.unit_id"
                          :options="units"
                          :reduce="unit => unit.id"
                          label="name"
                          :disabled="!rule.center_id"
                          :placeholder="$t('Todas')"
                      />
                    </div>
                    <div class="form-group col-md-4">
                      <label>{{ $t('Veículos') }}:</label>
                      <v-select
                          v-model="rule.vehicle_ids"
                          :options="vehicleOptions"
                          :reduce="vehicle => vehicle.id"
                          label="label"
                          multiple
                          :placeholder="$t('Todos')"
                      />
                    </div>
                  </div>

                  <div class="block text-right">
                    <b-button variant="outline-secondary" class="btn btn-back" @click="$router.go(-1)">
                      <i class="fa fa-arrow-left"></i> {{ $t('To Go Back') }}
                    </b-button>
                    <b-button type="submit" variant="primary" class="ml-1" :disabled="saving">
                      {{ $t('Save') }}
                      <i class="fa fa-save"></i>
                    </b-button>
                  </div>
                </b-form>
              </ValidationObserver>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'
import vSelect from 'vue-select'
import axios from 'axios'
import { CRITICALITIES } from './moviasEvents'

extend('required', required)

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      rule: {
        name: '',
        movias_event_id: null,
        movias_event_name: null,
        conditions: [],
        criticality: 'medium',
        cooldown_minutes: 5,
        active: true,
        center_id: null,
        unit_id: null,
        vehicle_ids: [],
      },
      events: [],
      loadingEvents: false,
      fields: [],
      centers: [],
      units: [],
      vehicles: [],
      saving: false,
    }
  },
  computed: {
    api() {
      return this.$store.state.api
    },
    eventOptions() {
      return this.events.map(event => ({ id: event.id, name: event.name, label: `${event.id} — ${event.name}` }))
    },
    fieldOptions() {
      return this.fields.map(field => ({ value: field.key, text: field.label }))
    },
    criticalityOptions() {
      return Object.entries(CRITICALITIES).map(([value, item]) => ({ value, text: item.label }))
    },
    vehicleOptions() {
      return this.vehicles
        .filter(vehicle => !this.rule.center_id || Number(vehicle.center_id) === Number(this.rule.center_id))
        .filter(vehicle => !this.rule.unit_id || Number(vehicle.unit_id) === Number(this.rule.unit_id))
        .map(vehicle => ({
          id: vehicle.id,
          label: vehicle.name && vehicle.name !== vehicle.license_plate
            ? `${vehicle.license_plate} — ${vehicle.name}`
            : vehicle.license_plate,
        }))
    },
  },
  mounted() {
    this.loadFields()
    this.loadEvents()
    this.loadCenters()
    this.loadVehicles()

    if (this.$route.params.id) {
      this.loadRule(this.$route.params.id)
    }
  },
  methods: {
    field(key) {
      return this.fields.find(item => item.key === key) || null
    },
    isBoolean(key) {
      const field = this.field(key)

      return !!field && field.type === 'boolean'
    },
    booleanOptions(key) {
      const field = this.field(key) || {}

      return [
        { value: true, text: field.on || this.$t('Ligado') },
        { value: false, text: field.off || this.$t('Desligado') },
      ]
    },
    unitOf(key) {
      const field = this.field(key)

      return field && field.unit ? field.unit : null
    },
    operatorsOf(key) {
      const field = this.field(key)

      return field ? field.operators : []
    },
    addCondition() {
      const first = this.fields[0]

      this.rule.conditions.push({
        field: first ? first.key : 'speed',
        operator: first ? first.operators[0] : '>',
        value: first && first.type === 'boolean' ? true : '',
      })
    },
    onFieldChange(condition) {
      const field = this.field(condition.field)

      condition.operator = field.operators[0]
      condition.value = field.type === 'boolean' ? true : ''
    },
    onCenterChange() {
      this.rule.unit_id = null
      this.loadUnits(this.rule.center_id)
    },
    loadFields() {
      axios.get(`${this.api}movias/event-fields`)
        .then(response => {
          this.fields = response.data.fields || []
        })
        .catch(error => this.$error(error))
    },
    loadEvents() {
      this.loadingEvents = true
      axios.get(`${this.api}movias/event-types`)
        .then(response => {
          this.events = response.data.data || []
        })
        .catch(error => this.$error(error))
        .finally(() => {
          this.loadingEvents = false
        })
    },
    loadCenters() {
      axios.get(`${this.api}centers`, { params: { paginated: false } })
        .then(response => {
          this.centers = response.data.data || []
        })
        .catch(error => this.$error(error))
    },
    loadUnits(centerId) {
      this.units = []

      if (!centerId) return

      axios.get(`${this.api}units`, { params: { paginated: false, center_id: centerId } })
        .then(response => {
          this.units = response.data.data || []
        })
        .catch(error => this.$error(error))
    },
    loadVehicles() {
      axios.get(`${this.api}vehicles`, { params: { paginated: false, status: 1 } })
        .then(response => {
          this.vehicles = response.data.data || []
        })
        .catch(error => this.$error(error))
    },
    loadRule(id) {
      axios.get(`${this.api}moviasEventRules/${id}`, { params: { with: ['vehicles'] } })
        .then(response => {
          const rule = response.data.data[0]

          this.rule = {
            id: rule.id,
            name: rule.name,
            movias_event_id: rule.movias_event_id,
            movias_event_name: rule.movias_event_name,
            conditions: rule.conditions || [],
            criticality: rule.criticality,
            cooldown_minutes: rule.cooldown_minutes,
            active: !!rule.active,
            center_id: rule.center_id,
            unit_id: rule.unit_id,
            vehicle_ids: (rule.vehicles || []).map(vehicle => vehicle.id),
          }
          this.loadUnits(rule.center_id)
        })
        .catch(error => this.$error(error))
    },
    async handleSubmit(validate) {
      if (await validate()) {
        this.save()
      }
    },
    payload() {
      const event = this.events.find(item => item.id === this.rule.movias_event_id)

      return {
        ...this.rule,
        movias_event_name: event ? event.name : (this.rule.movias_event_name || null),
      }
    },
    save() {
      let api = `${this.api}moviasEventRules`
      const data = this.payload()

      if (this.rule.id) {
        data._method = 'PUT'
        api += `/${this.rule.id}`
      }

      this.saving = true
      axios.post(api, data)
        .then(() => {
          this.$message(this.$t('Success'), this.$t('Information saved successfully'), 'success')
          this.$router.push('/moviasEventRules')
        })
        .catch(error => this.$error(error))
        .finally(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.required {
  color: red !important;
}
</style>
